/**
 * EntityFormActions compound sub-component.
 *
 * Renders the action button bar for the EntityForm. Supports submit, cancel,
 * reset, and custom action buttons. Configuration is resolved by deep-merging
 * DEFAULT_ACTIONS, the form config's `actions` block, and any runtime
 * `overrides` prop (last-wins).
 *
 * @see Spec section 3.7 for the component contract.
 * @see types.ts `ActionsConfig` and `ActionButtonConfig` for shape details.
 */

import * as React from 'react';

import { Button } from '@components/base/actions/Button/Button';
import { useEntityFormContext } from '../context/EntityFormContext';
import { DEFAULT_ACTIONS } from '../constants';
import type {
  EntityFormActionsProps,
  ActionsConfig,
  ActionButtonConfig,
  CustomActionConfig,
} from '../types';

// ---------------------------------------------------------------------------
// Alignment Mapping
// ---------------------------------------------------------------------------

/**
 * Maps the `ActionsConfig.alignment` value to a Tailwind `justify-*` class.
 */
const ALIGNMENT_CLASS_MAP: Record<
  NonNullable<ActionsConfig['alignment']>,
  string
> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
  'space-between': 'justify-between',
};

// ---------------------------------------------------------------------------
// Merge Helpers
// ---------------------------------------------------------------------------

/**
 * Deep-merges a single action button config from three layers:
 * DEFAULT_ACTIONS[key] -> config.actions[key] -> overrides[key].
 *
 * Each layer is optional; `undefined` values in a later layer do not
 * overwrite earlier ones.
 *
 * @param base     - The default action button config.
 * @param config   - The config-level action button override.
 * @param override - The prop-level action button override.
 * @returns The fully resolved action button config.
 */
function mergeActionButton(
  base: ActionButtonConfig | undefined,
  config: ActionButtonConfig | undefined,
  override: ActionButtonConfig | undefined,
): ActionButtonConfig {
  return {
    ...base,
    ...config,
    ...override,
  };
}

/**
 * Merges three layers of `ActionsConfig` into one resolved config:
 * DEFAULT_ACTIONS -> config.actions -> overrides.
 *
 * Button-level keys (submit, cancel, reset) are individually merged.
 * Custom actions and alignment are taken from the last defined layer.
 *
 * @param configActions - The actions block from the form config.
 * @param overrides     - Runtime overrides passed via the `overrides` prop.
 * @returns The fully resolved actions configuration.
 */
function resolveActions(
  configActions: ActionsConfig | undefined,
  overrides: Partial<ActionsConfig> | undefined,
): ActionsConfig {
  return {
    submit: mergeActionButton(
      DEFAULT_ACTIONS.submit,
      configActions?.submit,
      overrides?.submit,
    ),
    cancel: mergeActionButton(
      DEFAULT_ACTIONS.cancel,
      configActions?.cancel,
      overrides?.cancel,
    ),
    reset: mergeActionButton(
      DEFAULT_ACTIONS.reset,
      configActions?.reset,
      overrides?.reset,
    ),
    custom: overrides?.custom ?? configActions?.custom ?? DEFAULT_ACTIONS.custom,
    alignment:
      overrides?.alignment ?? configActions?.alignment ?? DEFAULT_ACTIONS.alignment,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders the action button bar for an EntityForm.
 *
 * Consumes form state from `useEntityFormContext()` and merges the
 * resolved action config to produce submit, cancel, reset, and custom
 * action buttons. Supports a `children` prop for additional custom content.
 *
 * @example
 * ```tsx
 * <EntityForm config={config} form={form}>
 *   <EntityForm.Fields />
 *   <EntityForm.Actions />
 * </EntityForm>
 *
 * // With overrides:
 * <EntityForm.Actions overrides={{ submit: { label: 'Save Draft' } }} />
 *
 * // With custom children:
 * <EntityForm.Actions>
 *   <span className="text-sm text-gray-500">* Required fields</span>
 * </EntityForm.Actions>
 * ```
 */
export const EntityFormActions: React.FC<EntityFormActionsProps> = ({
  overrides,
  className,
  children,
}) => {
  const { config, form } = useEntityFormContext();

  // ---- Resolve merged actions config --------------------------------------

  const actions = resolveActions(config.actions, overrides);
  const alignmentClass =
    ALIGNMENT_CLASS_MAP[actions.alignment ?? 'right'];
  const cssOverride = config.cssOverrides?.actions;

  // ---- Render submit button -----------------------------------------------

  const submitButton = actions.submit?.visible !== false && (
    <Button
      type="submit"
      variant={actions.submit?.variant ?? 'primary'}
      size={actions.submit?.size}
      loading={form.isSubmitting}
      disabled={form.isSubmitting}
      className={actions.submit?.className}
    >
      {actions.submit?.label ?? 'Submit'}
    </Button>
  );

  // ---- Render cancel button -----------------------------------------------

  const cancelButton = actions.cancel?.visible !== false && (
    <Button
      type="button"
      variant={actions.cancel?.variant ?? 'ghost'}
      size={actions.cancel?.size}
      onClick={form.cancel}
      className={actions.cancel?.className}
    >
      {actions.cancel?.label ?? 'Cancel'}
    </Button>
  );

  // ---- Render reset button ------------------------------------------------

  const resetButton = actions.reset?.visible === true && (
    <Button
      type="button"
      variant={actions.reset.variant ?? 'secondary'}
      size={actions.reset.size}
      onClick={() => { form.reset(); }}
      className={actions.reset.className}
    >
      {actions.reset.label ?? 'Reset'}
    </Button>
  );

  // ---- Render custom action buttons ---------------------------------------

  const customButtons = actions.custom?.map(
    (action: CustomActionConfig) => (
      <Button
        key={action.id}
        type="button"
        variant={action.variant ?? 'secondary'}
        size={action.size}
        className={action.className}
      >
        {action.label ?? action.id}
      </Button>
    ),
  );

  // ---- Compose layout -----------------------------------------------------

  const containerClasses = [
    'flex flex-wrap items-center gap-3 pt-4',
    alignmentClass,
    cssOverride,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClasses}>
      {children}
      {resetButton}
      {customButtons}
      {cancelButton}
      {submitButton}
    </div>
  );
};

EntityFormActions.displayName = 'EntityFormActions';
